





























































































































































































































































































































import Vue from 'vue';
import { Grid } from '@progress/kendo-vue-grid';
import dayjs from 'dayjs';
import http, { handleApiResponse } from '@/services/http';
import eventBus from '@/services/eventBus';
import ApiResponse from '@/models/ApiResponse';
import auth from '@/services/auth';
import IconButton from '@/components/IconButton.vue';
import LookupComboBox from './LookupComboBox.vue';
import GeneralLedgerMatchingItemPicker from './GeneralLedgerMatchingItemPicker.vue';
import ValidationError from '@/modules/downPayment/components/ValidationError.vue';
import basket from '../services/ReconBasketStore';

interface Data {
  selectedItem: any;
  years: any[];
  statuses: any[];
  positiveItems: any[];
  negativeItems: any[];
  invoiceMirrors: any[];
  selectableItems: any[];
  selectableSide: string | null;
  pickerOpen: boolean;
}

interface Methods {
  initEditor(): Promise<void>;
  save(): void;
  cancel(): void;
  addItem(side: string): void;
  removeItem(item: any): void;
  toggleDeleting(item: any): void;
  addSelectedItems(items: any[]): void;
  onAccNoValueChanged(newValue: any): void;
  loadInvoiceMirrors(): void;
  formatTotal(value: any): string;
}

interface Computed {
  isEdit: boolean;
  isFromBasket: boolean;
  showAccno: boolean;
  balance: number;
  positiveItemsTotal: string;
  negativeItemsTotal: string;
  error: string;
  itemColumns: any[];
  selectedYearNumber: number | null;
}

// Új pontozás kiinduló állapota; a Mégse gomb is erre állít vissza
const defaultSelectedItem = () => ({
  id: 0,
  code: '',
  remark: '',
  accno: '-',
  year: null,
  status: null,
  balance: 0,
  fixAssets: 1,
  insDate: dayjs(new Date()).format('YYYY-MM-DD'),
  insTime: dayjs(new Date()).format('HH:mm'),
  createdBy: auth.userName,
});

export default Vue.extend<Data, Methods, Computed, {}>({
  name: 'GeneralLedgerMatchingEditor',

  components: {
    Grid,
    IconButton,
    GeneralLedgerMatchingItemPicker,
    LookupComboBox,
    ValidationError,
  },

  data(): Data {
    return {
      selectedItem: defaultSelectedItem(),
      years: [],
      statuses: [],
      positiveItems: [],
      negativeItems: [],
      invoiceMirrors: [],
      selectableItems: [],
      selectableSide: null,
      pickerOpen: false,
    };
  },

  watch: {
    pickerOpen(open: boolean) {
      if (!open) {
        this.selectableSide = null;
        this.selectableItems = [];
      }
    },

    'selectedItem.year'() {
      // Évváltáskor a hozzárendelt tételek már nem érvényesek, ezért törlődnek
      this.positiveItems = [];
      this.negativeItems = [];

      this.loadInvoiceMirrors();
      if (this.selectableSide) {
        this.addItem(this.selectableSide);
      }
    },
  },

  async beforeMount() {
    this.years = await http
      .get<ApiResponse<any[]>>('general-ledger-recon/years')
      .then(handleApiResponse);
    this.statuses = await http
      .get<ApiResponse<any[]>>('general-ledger-recon/statuses')
      .then(handleApiResponse);

    await this.initEditor();
  },

  methods: {
    // Szerkesztésnél a mentett állapotot tölti vissza a szerverről, új pontozásnál a kiinduló állapotot adja
    async initEditor() {
      if (this.isEdit) {
        this.selectedItem = await http
          .get<ApiResponse<any>>(`general-ledger-recon/${this.$route.params.id}`)
          .then(handleApiResponse);

        // Folytatott összepontozás a kosárból: a fej a szerverről, a tételek a kosár aktuális tartalmából
        // (a felhasználó a böngészőben bővítette/szűkítette); egyébként a mentett tételek
        if (this.isFromBasket && basket.continued && basket.continued.id === Number(this.$route.params.id)) {
          // Az év-watcher a következő tickben üríti a táblákat — a tételek csak utána tölthetők
          await this.$nextTick();
          this.positiveItems = basket.positiveItems.map((i: any) => ({ ...i }));
          this.negativeItems = basket.negativeItems.map((i: any) => ({ ...i }));
        } else {
          const items = await http
            .get<ApiResponse<any>>(`general-ledger-recon/${this.$route.params.id}/items`)
            .then(handleApiResponse);

          this.positiveItems = items.positiveItems;
          this.negativeItems = items.negativeItems;
        }
      } else {
        this.selectedItem = defaultSelectedItem();
        this.positiveItems = [];
        this.negativeItems = [];

        const currentYear = this.years.find((y: any) => y.year === String(new Date().getFullYear()));
        this.selectedItem.year = currentYear ? currentYear.id : null;

        const defaultStatus = this.statuses.find((s: any) => s.name === 'Feldolgozás alatt');
        this.selectedItem.status = defaultStatus ? defaultStatus.id : null;

        // Főkönyvi számos összepontozásnál az accno kitölthető, simánál fixen "-"
        if (this.$route.query.withAccno === 'true') {
          this.selectedItem.accno = '';
        }

        // Összepontozó kosárból (tranzakció-böngésző "Véglegesítés"): az év a kosár tételeiből,
        // a tartozik/követel táblák a kosár tartalmával indulnak; fksz-es módban a közös fksz előtöltve
        if (this.isFromBasket && basket.items.length) {
          const basketYear = this.years.find((y: any) => y.year === String(basket.year));
          if (basketYear) {
            this.selectedItem.year = basketYear.id;
          }
          if (this.showAccno && basket.commonAccno) {
            this.selectedItem.accno = basket.commonAccno;
          }
          // Az év-watcher a következő tickben üríti a táblákat — a tételek csak utána tölthetők
          await this.$nextTick();
          this.positiveItems = basket.positiveItems.map((i: any) => ({ ...i }));
          this.negativeItems = basket.negativeItems.map((i: any) => ({ ...i }));
        }
      }
    },

    async save() {
      if (this.error !== '') {
        return;
      }

      const itemIds = [...this.positiveItems, ...this.negativeItems].map((i: any) => i.id);
      const ret = await http
        .post<ApiResponse<number>>('general-ledger-recon/save', {
          id: this.selectedItem.id,
          code: this.selectedItem.code,
          remark: this.selectedItem.remark,
          year: this.selectedItem.year,
          accno: this.selectedItem.accno,
          status: this.selectedItem.status,
          balance: this.balance,
          itemIds,
        })
        .then(handleApiResponse);

      eventBus.$emit('success', 'Sikeres mentés!');

      // A kosár tartalma mentéskor "elfogyott" és az összepontozás-mód is véget ér (a böngésző a
      // következő megnyitásnál a mód előtti szűrőket állítja vissza); Mégse esetén mindkettő megmarad
      if (this.isFromBasket) {
        basket.clear();
        basket.endMode();
      }

      // Új pontozásnál átdobjuk a szerkesztő oldalra; a showAccno az accno-ból dől el,
      // így a főkönyvi számos és a sima pontozás is a saját nézetét kapja
      if (!this.isEdit) {
        this.selectedItem.id = ret;
        this.$router.push(`/ledger-matching/edit/${ret}`);
      }
    },

    // Mégse: vissza oda, ahonnan jöttünk (a mentetlen módosítások elvesznek).
    // Kosárból (fromBasket) a tranzakció böngészőbe — a kosár és az összepontozás-mód megmarad,
    // így a felhasználó folytathatja a tételek válogatását (user-döntés 2026-09-07).
    // Egyébként a pontozás böngészőbe; főkönyvi számos módban annak "Főkönyvi számmal" fülére.
    cancel() {
      if (this.isFromBasket) {
        this.$router.push('/transactions');
        return;
      }
      this.$router.push(this.showAccno
        ? { path: '/ledger-matching', query: { mode: 'accno' } }
        : '/ledger-matching');
    },

    async addItem(side) {
      const year = this.years.find((y: any) => y.id === this.selectedItem.year);
      if (!year) {
        return;
      }

      // Főkönyvi számos összepontozásnál csak a megadott főkönyvi szám tételei választhatók
      let url = `general-ledger-recon/selectable-items/${year.year}/${side === 'positive'}`;
      if (this.showAccno) {
        const accno = String(this.selectedItem.accno || '').trim();
        if (!accno || accno === '-') {
          return;
        }
        url += `?accno=${encodeURIComponent(accno)}`;
      }

      this.selectableSide = side;
      const items = await http
        .get<ApiResponse<any[]>>(url)
        .then(handleApiResponse);

      // A táblában már szereplő tételek ne jelenjenek meg a választóban
      const target = side === 'positive' ? this.positiveItems : this.negativeItems;
      this.selectableItems = items.filter((i: any) => !target.some((t: any) => t.id === i.id));
      this.pickerOpen = true;
    },

    // A választóban kijelölt tételek átkerülnek az adott oldal táblájába (a keresztmódú
    // figyelmeztetés már a választóban, pipálásnál lefutott)
    addSelectedItems(items) {
      const target = this.selectableSide === 'positive' ? this.positiveItems : this.negativeItems;
      items.forEach((item: any) => {
        if (!target.some((t: any) => t.id === item.id)) {
          target.push(item);
        }
      });
    },

    toggleDeleting(item) {
      this.$set(item, 'deleting', !item.deleting);
    },

    removeItem(item) {
      const posIdx = this.positiveItems.indexOf(item);
      if (posIdx !== -1) {
        this.positiveItems.splice(posIdx, 1);
        return;
      }
      const negIdx = this.negativeItems.indexOf(item);
      if (negIdx !== -1) {
        this.negativeItems.splice(negIdx, 1);
      }
    },

    onAccNoValueChanged(newValue) {
      this.selectedItem.accno = newValue.accno;
      if (this.selectableSide) {
        this.addItem(this.selectableSide);
      }
    },

    async loadInvoiceMirrors() {
      if (!this.showAccno) {
        return;
      }

      const year = this.years.find((y: any) => y.id === this.selectedItem.year);
      if (!year) {
        this.invoiceMirrors = [];
        return;
      }

      this.invoiceMirrors = await http
        .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/invoice-mirror/${year.year}`)
        .then(handleApiResponse);
    },

    formatTotal(value) {
      return new Intl.NumberFormat('hu-HU').format(Number(value || 0));
    },
  },

  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },

    // Az összepontozó kosárból (tranzakció-böngésző) érkező létrehozás VAGY folytatás (meglévő csoport szerkesztése)
    isFromBasket() {
      return this.$route.query.fromBasket === 'true';
    },

    showAccno() {
      if (this.isEdit) {
        return this.selectedItem.accno != null && String(this.selectedItem.accno).trim() !== '-';
      }
      return this.$route.query.withAccno === 'true';
    },

    balance() {
      const positiveTotal = this.positiveItems.reduce((sum: number, i: any) => sum + Number(i.total || 0), 0);
      const negativeTotal = this.negativeItems.reduce((sum: number, i: any) => sum + Number(i.total || 0), 0);
      return  positiveTotal - negativeTotal;
    },

    positiveItemsTotal() {
      return new Intl.NumberFormat('hu-HU').format(this.positiveItems.reduce((sum: number, i: any) => sum + Number(i.total || 0), 0));
    },

    negativeItemsTotal() {
      return new Intl.NumberFormat('hu-HU').format(this.negativeItems.reduce((sum: number, i: any) => sum + Number(i.total || 0), 0));
    },

    error() {
      if (!String(this.selectedItem.remark || '').trim()) {
        return 'Megjegyzés mező kitöltése kötelező!';
      }
      if (this.showAccno) {
        const accno = String(this.selectedItem.accno || '').trim();
        if (!accno || accno === '-') {
          return 'Főkönyvi szám mező kitöltése kötelező!';
        }

        const status = this.statuses.find((s: any) => s.id === this.selectedItem.status);
        if (status && String(status.name).toLowerCase().startsWith('összepontoz') && this.balance !== 0) {
          return 'Összepontozott állapot csak 0 egyenleg esetén menthető!';
        }
      }
      return '';
    },

    itemColumns: () => [
      {
        field: '',
        width: '75px',
        cell: 'deleteCell',
      },
      {
        field: 'accno',
        title: 'Főkönyvi szám',
        width: '120px',
      },
      {
        field: 'total',
        title: 'Összeg',
        cell: 'totalCell',
        width: '130px',
      },
      {
        field: 'accDate',
        title: 'Gazd. es. dátuma',
        width: '130px',
        className: 'right_text',
      },
      // Szélesség nélkül: a gazdasági esemény tölti ki a maradék helyet (flex oszlop)
      {
        field: 'accEvent',
        title: 'Gazd. esemény',
      },
      {
          filterable: false,
          width: 0,
      }
    ],

    // A választó dátum-előtöltéséhez a pontozás könyvelési éve (mmbokyear.year)
    selectedYearNumber(): number | null {
      const year = this.years.find((y: any) => y.id === this.selectedItem.year);
      return year ? Number(year.year) : null;
    },
  },
});
